import { forwardRef, HTMLProps, ReactNode } from 'react';

import Link from 'next/link';
import { tv } from 'tailwind-variants';

import cn from '@/helpers/utils/classNames';

import '@/styles/globals.v2.css';

type CustomButtonLink = Omit<HTMLProps<HTMLAnchorElement>, 'ref'>;

export interface ButtonLinkProps extends CustomButtonLink {
  scheme: 'primary' | 'secondary';
  variant: 'outlined' | 'filled';
  as?: 'a' | 'Link';
  children: ReactNode;
}

export const buttonStyles = tv({
  base: 'v2-border v2-whitespace-nowrap v2-w-full v2-transition-colors v2-p-2 v2-px-4 v2-text-base hover:v2-transition-colors v2-rounded',
  variants: {
    primary: {
      filled:
        'v2-border-transparent v2-text-white v2-bg-primary v2-outline-grayscale-40 hover:v2-text-primary hover:v2-border-primary hover:v2-bg-white active:v2-shadow-button active:v2-bg-none active:v2-outline-0',
      outlined:
        'v2-bg-white v2-text-primary v2-border-primary active:v2-shadow-button active:v2-bg-none active:v2-outline-0',
    },
    secondary: {
      filled:
        'v2-border-transparent v2-text-white v2-bg-grayscale-40 hover:v2-text-grayscale-40 v2-outline-primary hover:v2-border-grayscale-40 hover:v2-bg-transparent',
      outlined: 'v2-bg-transparent v2-text-grayscale-70 v2-border-grayscale-30 hover:v2-text-primary',
    },
  },
});

const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(function ButtonLink(props, ref) {
  const { as, scheme, type, variant, disabled, className, href = '#', children, ...restProps } = props;

  if (as === 'a')
    return (
      <a
        href={href}
        className={cn(
          buttonStyles({ [scheme]: variant }),
          {
            'v2-cursor-not-allowed v2-font-sweet-sans-pro v2-border-transparent v2-bg-grayscale-60 v2-text-white hover:v2-border-transparent hover:v2-bg-grayscale-70 hover:v2-text-white focus:v2-bg-grayscale-70 focus:v2-text-white':
              disabled,
          },
          className
        )}
      >
        {children}
      </a>
    );

  return (
    <Link
      ref={ref}
      href={href}
      className={cn(
        buttonStyles({ [scheme]: variant }),
        {
          'v2-cursor-not-allowed v2-font-sweet-sans-pro v2-border-transparent v2-bg-grayscale-60 v2-text-white hover:v2-border-transparent hover:v2-bg-grayscale-70 hover:v2-text-white focus:v2-bg-grayscale-70 focus:v2-text-white':
            disabled,
        },
        className
      )}
      prefetch={false}
      {...restProps}
    >
      {children}
    </Link>
  );
});

export default ButtonLink;
